/**
 * API Client
 * Zentraler HTTP-Client für alle API-Calls
 * 
 * Features:
 * - Session Cookie (JSESSIONID) Support
 * - Einheitliches Response-Format
 * - Error Handling
 * - Cursor-basierte Pagination Helper
 */

import { API_BASE_URL, MEDIA_BASE_URL } from '../config/constants';

/**
 * Standard API Response Format:
 * Success: { success: true, data: {} }
 * Error: { success: false, error: { code: string, message: string, details?: {} } }
 */

/**
 * Basis Fetch-Wrapper mit Error-Handling
 * @param {string} endpoint - API Endpoint (ohne Base URL)
 * @param {object} options - Fetch Options
 * @returns {Promise<{success: boolean, data?: any, error?: {code: string, message: string, details?: any}}>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Wichtig für JSESSIONID Cookie
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Accept': 'application/json',
        ...options.headers,
      },
    });

    // Content-Type prüfen
    const contentType = response.headers.get('content-type');
    
    // Nicht-JSON Responses (z.B. 204 No Content)
    if (!contentType || !contentType.includes('application/json')) {
      if (response.ok) {
        return { success: true, data: {} };
      }
      return {
        success: false,
        error: { code: `HTTP_${response.status}`, message: `HTTP Error: ${response.status}` }
      };
    }

    const responseData = await response.json();

    // API v1.2 Standard Response Format
    if (typeof responseData.success === 'boolean') {
      if (responseData.success) {
        return { success: true, data: responseData.data };
      } else {
        return {
          success: false,
          error: responseData.error || { code: 'UNKNOWN_ERROR', message: 'Unbekannter Fehler' }
        };
      }
    }

    // Fallback für unerwartete Response-Formate
    if (response.ok) {
      return { success: true, data: responseData };
    }
    
    return {
      success: false,
      error: { code: `HTTP_${response.status}`, message: responseData.message || `HTTP Error: ${response.status}` }
    };

  } catch (error) {
    console.error(`API Request Error [${endpoint}]:`, error);
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: error.message || 'Netzwerkfehler' }
    };
  }
};

/**
 * GET Request
 * @param {string} endpoint - API Endpoint
 * @param {object} params - Query Parameter (optional)
 */
export const get = (endpoint, params = {}) => {
  const queryString = buildQueryString(params);
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  return apiRequest(url, { method: 'GET' });
};

/**
 * POST Request
 * @param {string} endpoint - API Endpoint
 * @param {object} body - Request Body
 */
export const post = (endpoint, body = {}) =>
  apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * PUT Request (vollständiges Update)
 * @param {string} endpoint - API Endpoint
 * @param {object} body - Request Body
 */
export const put = (endpoint, body) =>
  apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/**
 * PATCH Request (partielles Update)
 * @param {string} endpoint - API Endpoint
 * @param {object} body - Request Body (partial)
 */
export const patch = (endpoint, body) =>
  apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/**
 * DELETE Request
 * @param {string} endpoint - API Endpoint
 */
export const del = (endpoint) =>
  apiRequest(endpoint, { method: 'DELETE' });

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Baut Query-String aus Objekt
 * @param {object} params - Key-Value Pairs
 * @returns {string} - Query String (ohne ?)
 */
export const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, value);
    }
  });
  
  return searchParams.toString();
};

/**
 * Baut Cursor-Pagination Parameter
 * @param {object} options - { cursor?, limit? }
 * @returns {object} - Query Parameter Objekt
 */
export const buildCursorParams = (options = {}) => {
  const params = {};
  
  if (options.cursor) {
    params.cursor = options.cursor;
  }
  
  // Default: 200, Max: 1000
  params.limit = Math.min(options.limit || 200, 1000);
  
  return params;
};

/**
 * Konvertiert media_key zu vollständiger URL
 * @param {string} mediaKey - z.B. "cover/manga/1.png"
 * @returns {string} - Vollständige Media URL
 */
export const getMediaUrl = (mediaKey) => {
  if (!mediaKey) return null;
  // Wenn MEDIA_BASE_URL gesetzt: CDN-Pfad verwenden
  if (MEDIA_BASE_URL) return `${MEDIA_BASE_URL}/${mediaKey}`;
  // Fallback: über API-Endpoint (GET /media/{key})
  return `${API_BASE_URL}/media/${mediaKey}`;
};

/**
 * Alternative: Media URL direkt über API Endpoint
 * @param {string} mediaKey - z.B. "cover/manga/1.png"
 * @returns {string} - API Media URL
 */
export const getMediaApiUrl = (mediaKey) => {
  if (!mediaKey) return null;
  return `${API_BASE_URL}/media/${mediaKey}`;
};

// ============================================================================
// ERROR HELPERS
// ============================================================================

/**
 * Prüft ob ein Fehler ein bestimmter Code ist
 * @param {object} error - Error Objekt
 * @param {string} code - Error Code
 * @returns {boolean}
 */
export const isErrorCode = (error, code) => {
  return error?.code === code;
};

/**
 * Standard Error Codes
 */
export const ErrorCodes = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_ISBN: 'INVALID_ISBN',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  PRIVATE_RESOURCE: 'PRIVATE_RESOURCE',
  NOT_FOUND: 'NOT_FOUND',
  USER_EXISTS: 'USER_EXISTS',
  USERNAME_EXISTS: 'USERNAME_EXISTS',
  ALREADY_OWNED: 'ALREADY_OWNED',
  REQUEST_EXISTS: 'REQUEST_EXISTS',
  ALREADY_FRIENDS: 'ALREADY_FRIENDS',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  RATE_LIMITED: 'RATE_LIMITED',
  NETWORK_ERROR: 'NETWORK_ERROR',
};
